import * as signalR from '@microsoft/signalr';
import { getToken } from 'src/utils/Common';

let url = process.env.REACT_APP_BACKEND_URL;

class PaymentSignalRService {
    constructor() {
        this.connection = null;
        this.connectionStarted = false;
        this.applicationReference = null;
        this.callbacks = {
            onPaymentUpdate: null,
            onPaymentError: null,
            onConnectionStateChange: null
        };
    }

    // Initialize SignalR connection
    async initializeConnection() {
        if (this.connection && this.connectionStarted) {
            return;
        }

        const AuthToken = getToken();

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(`${url}paymentHub`, {
                accessTokenFactory: () => `${AuthToken}`
            })
            .withAutomaticReconnect([0, 2000, 10000, 30000])
            .build();

        // Connection state change handler
        this.connection.onreconnecting((error) => {
            console.log('Payment SignalR reconnecting:', error);
            this.connectionStarted = false;
            if (this.callbacks.onConnectionStateChange) {
                this.callbacks.onConnectionStateChange('reconnecting', error);
            }
        });

        this.connection.onreconnected((connectionId) => {
            console.log('Payment SignalR reconnected:', connectionId);
            this.connectionStarted = true;
            if (this.callbacks.onConnectionStateChange) {
                this.callbacks.onConnectionStateChange('connected', null);
            }
            // Rejoin group after reconnection
            if (this.applicationReference) {
                this.joinGroup(this.applicationReference);
            }
        });

        this.connection.onclose((error) => {
            console.log('Payment SignalR connection closed:', error);
            this.connectionStarted = false;
            if (this.callbacks.onConnectionStateChange) {
                this.callbacks.onConnectionStateChange('disconnected', error);
            }
        });

        // Payment update event handler
        this.connection.on('ReceivePaymentUpdate', (isSuccess, message, paymentData) => {
            console.log('Payment update received:', { isSuccess, message, paymentData });
            if (this.callbacks.onPaymentUpdate) {
                this.callbacks.onPaymentUpdate(isSuccess, message, paymentData);
            }
        });

        // Payment error event handler
        this.connection.on('ReceivePaymentError', (errorMessage) => {
            console.log('Payment error received:', errorMessage);
            if (this.callbacks.onPaymentError) {
                this.callbacks.onPaymentError(errorMessage);
            }
        });

        try {
            await this.connection.start();
            this.connectionStarted = true;
            console.log('Payment SignalR connected successfully');
            if (this.callbacks.onConnectionStateChange) {
                this.callbacks.onConnectionStateChange('connected', null);
            }
        } catch (error) {
            console.error('Payment SignalR connection failed:', error);
            this.connectionStarted = false;
            if (this.callbacks.onConnectionStateChange) {
                this.callbacks.onConnectionStateChange('disconnected', error);
            }
            throw error;
        }
    }

    // Check if connection is ready
    isConnected() {
        return this.connection &&
            this.connectionStarted &&
            this.connection.state === signalR.HubConnectionState.Connected;
    }

    // Join payment group
    async joinGroup(applicationReference) {
        // Wait for connection to be established
        let retryCount = 0;
        const maxRetries = 15;

        while (!this.isConnected() && retryCount < maxRetries) {
            if (retryCount === 0) {
                console.log('Connection not ready, initializing...');
                try {
                    await this.initializeConnection();
                } catch (error) {
                    console.error('Failed to initialize connection:', error);
                    throw error;
                }
            } else {
                console.log(`Waiting for connection... (${retryCount}/${maxRetries})`);
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
            retryCount++;
        }

        if (!this.isConnected()) {
            throw new Error('Failed to establish SignalR connection after multiple attempts');
        }

        try {
            await this.connection.invoke('JoinGroup', applicationReference);
            this.applicationReference = applicationReference;
            console.log(`Joined payment group: ${applicationReference}`);
        } catch (error) {
            console.error('Failed to join payment group:', error);
            throw error;
        }
    }

    // Leave payment group
    async leaveGroup(applicationReference) {
        if (!this.isConnected()) {
            return;
        }

        try {
            await this.connection.invoke('LeaveGroup', applicationReference);
            console.log(`Left payment group: ${applicationReference}`);
        } catch (error) {
            console.error('Failed to leave payment group:', error);
        }
    }

    // Set callback functions
    setCallbacks(callbacks) {
        this.callbacks = { ...this.callbacks, ...callbacks };
    }

    // Disconnect and cleanup
    async disconnect() {
        if (this.connection) {
            if (this.applicationReference) {
                await this.leaveGroup(this.applicationReference);
            }
            await this.connection.stop();
            this.connection = null;
            this.connectionStarted = false;
            this.applicationReference = null;
        }
    }

    // Get connection state
    getConnectionState() {
        return this.connection?.state || 'Disconnected';
    }
}

// Create singleton instance
const paymentSignalRService = new PaymentSignalRService();

export default paymentSignalRService;
